import { teamMembers } from "@/mocks/site";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import Breadcrumb from "@/components/base/Breadcrumb";
import FeedbackForm from "@/components/feature/FeedbackForm";

const positioning = [
  {
    icon: "ri-focus-3-line",
    title: "一站式动漫资讯",
    desc: "聚合新番前瞻、深度解读、幕后科普与行业观察，帮你用一个站点跟上动漫动态。"
  },
  {
    icon: "ri-tv-2-line",
    title: "及时更新速递",
    desc: "按星期整理在番作品与集数进度，错过更新也能快速补上进度信息。"
  },
  {
    icon: "ri-user-star-line",
    title: "完整角色档案",
    desc: "角色设定、所属作品与关联资讯相互串联，浏览路径清晰不迷路。"
  },
  {
    icon: "ri-chat-smile-2-line",
    title: "活跃粉丝社区",
    desc: "新番讨论、推荐交流与创作分享，让每一次安利都能遇到同好。"
  }
];

export default function AboutPage() {
  useDocumentTitle(
    "关于我们 - 漫蛙2动漫资讯平台",
    "了解漫蛙2的网站定位、内容方向与团队介绍，并通过反馈表单与我们联系。"
  );
  return (
    <div className="pb-4">
      <section className="border-b border-background-200 bg-background-100">
        <div className="page-wrap py-8 md:py-12">
          <Breadcrumb items={[{ label: "关于我们" }]} />
          <h1 className="mt-4 text-2xl font-extrabold text-foreground-950 md:text-3xl">关于漫蛙2</h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            漫蛙2 是一个面向广大动漫爱好者的一站式综合动漫资讯平台。我们希望把「看资讯、追更新、查角色、聊话题」这些分散的动作，收拢到一个清晰、舒适的浏览体验里。
          </p>
        </div>
      </section>

      <section className="page-wrap mt-10 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {positioning.map((item) => (
          <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
            <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-600">
              <i className={`${item.icon} text-2xl`} aria-hidden="true"></i>
            </span>
            <h2 className="mt-4 text-base font-bold text-foreground-950">{item.title}</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
          </div>
        ))}
      </section>

      <section className="page-wrap mt-14">
        <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">我们的团队</h2>
        <p className="mt-2 text-sm text-foreground-600">以下成员为虚构示例，用于展示团队介绍模块。</p>
        <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {teamMembers.map((member) => (
            <div key={member.id} className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
              <div className="aspect-square w-full overflow-hidden bg-background-200">
                <img
                  src={member.avatar}
                  alt={`${member.name} 团队成员头像`}
                  title={`${member.name} - ${member.role}`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="p-4">
                <h3 className="text-base font-semibold text-foreground-950">{member.name}</h3>
                <p className="mt-0.5 text-xs font-medium text-primary-600">{member.role}</p>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{member.bio}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="page-wrap mt-14 grid grid-cols-1 gap-10 lg:grid-cols-3">
        <div className="lg:col-span-1">
          <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">联系我们</h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">
            如果你有内容建议、功能反馈或合作意向，欢迎通过右侧表单联系我们，也可以使用以下方式。
          </p>
          <ul className="mt-6 space-y-4">
            <li className="flex items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className="ri-mail-line"></i>
              </span>
              <div>
                <p className="text-sm font-medium text-foreground-900">邮箱</p>
                <p className="text-sm text-foreground-600">contact@manwa2.example.com</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className="ri-time-line"></i>
              </span>
              <div>
                <p className="text-sm font-medium text-foreground-900">响应时间</p>
                <p className="text-sm text-foreground-600">工作日 1-2 个工作日内回复</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className="ri-map-pin-line"></i>
              </span>
              <div>
                <p className="text-sm font-medium text-foreground-900">办公地点</p>
                <p className="text-sm text-foreground-600">线上协作团队 · 远程办公</p>
              </div>
            </li>
          </ul>
        </div>

        <div className="lg:col-span-2">
          <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">反馈表单</h2>
          <p className="mt-2 text-sm text-foreground-600">请填写以下信息，我们会认真阅读每一条反馈。</p>
          <div className="mt-5">
            <FeedbackForm />
          </div>
        </div>
      </section>
    </div>
  );
}